import React from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import PersonIcon from '@mui/icons-material/Person';
import { formatDate, formatUserName } from '../../utils/formatUtils';
import { getChipColor, getChipLabel, getProcessStageLabel } from '../../utils/processStageUtils';

/**
 * Shared language popover component for showing language details
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the popover is open
 * @param {HTMLElement} props.anchorEl The element to anchor the popover to
 * @param {Function} props.onClose Handler for when the popover is closed
 * @param {Array} props.languages List of languages with their status
 * @param {Array} props.videoLanguages Full video languages data with update information
 */
const LanguagePopover = ({ open, anchorEl, onClose, languages, videoLanguages = [] }) => {
  
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Box sx={{ p: 2, maxWidth: 350 }}>
        <Typography variant="subtitle2" gutterBottom>All Languages</Typography>
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {languages.map(lang => {
            // Find detailed language info from videoLanguages
            const langDetails = videoLanguages?.find(vl => vl.languageName === lang.name);
            
            return (
              <Box 
                key={lang.name} 
                sx={{ 
                  mb: 1.5, 
                  pb: 1.5, 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { 
                    borderBottom: 'none',
                    mb: 0,
                    pb: 0
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={getChipLabel(lang.name, lang.processStage)}
                    size="small"
                    sx={{ 
                      mr: 1,
                      fontSize: '0.7rem',
                      backgroundColor: `${getChipColor(lang.processStage)}20`,
                      color: getChipColor(lang.processStage),
                      borderColor: getChipColor(lang.processStage),
                      border: '1px solid',
                      fontWeight: 500
                    }}
                  />
                  <Typography variant="body2">
                    {getProcessStageLabel(lang.processStage)}
                  </Typography>
                </Box>
                
                {langDetails && (
                  <Box sx={{ pl: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Last updated by: {langDetails.lastModifiedBy || formatUserName(langDetails.updatedBy) || 'Unknown'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeFilledIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {langDetails.lastModifiedDate ? formatDate(langDetails.lastModifiedDate) : 'Unknown date'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Popover>
  );
};

export default LanguagePopover;